import { gql } from 'apollo-boost';

export const recommendationsGridClientFeaturesFragment = gql`
    fragment RecommendationsGridClientFeaturesFragment on GdClientFeatures {
        enabledRecommendationTypes
    }
`;

export const summaryRecommendationGraphFragment = gql`
    fragment summaryRecommendationGraphFragment on GdRecommendationKpiGraph {
        typeY
        typeX
        points {
            valueX
            typeValueX
            profitValue
            currentValue
            holiday
        }
    }
`;

export const summaryRecommendationItemsFragment = gql`
    fragment summaryRecommendationItemsFragment on GdRecommendationItems {
        rowset {
            clicks
            cost
            currency
            name
        }
    }
`;

export const summaryRecommendationKpiFragment = gql`
    fragment summaryRecommendationKpiFragment on GdRecommendationKpi {
        sign
        clicks
        cost
        period
        currency
        graph {
            ...summaryRecommendationGraphFragment
        }
    }

    ${summaryRecommendationGraphFragment}
`;

export const recommendationKeyFragment = gql`
    fragment recommendationKeyFragment on GdRecommendationKey {
        userKey1
        userKey2
        userKey3
        pid
        bid
        cid
        timestamp
        type
    }
`;

export const summaryRecommendationFragment = gql`
    fragment summaryRecommendationFragment on GdRecommendationSummary {
        type
        status
        isApplicable
        totalCount
        ... on GdRecommendationSummaryWithKpi {
            kpi {
                ...summaryRecommendationKpiFragment
                ... on GdRecommendationKpiDailyBudget {
                    recommendedDailyBudget
                }
            }
            items {
                ...summaryRecommendationItemsFragment
            }
        }
        campaignIds
        keys {
            ...recommendationKeyFragment
        }
    }

    ${summaryRecommendationItemsFragment}
    ${summaryRecommendationKpiFragment}
    ${recommendationKeyFragment}
`;

export const recommendationsDailyBudgetGridKpiItems = gql`
    fragment RecommendationsDailyBudgetGridKpiItemsFragment on GdRecommendationWithKpi {
        kpi {
            clicks
            cost
            currency
            ... on GdRecommendationKpiDailyBudget {
                recommendedDailyBudget
                currentDailyBudget
            }
        }
    }
`;

export const recommendationsDailyBudgetCampaignsRow = gql`
    fragment RecommendationsDailyBudgetCampaignsRowFragment on GdCampaign {
        stats {
            clicks
            cost
        }
        recommendations {
            status
            isApplicable
            ...RecommendationsDailyBudgetGridKpiItemsFragment
            keys {
                ...recommendationKeyFragment
            }
        }
        flatStrategy {
            budget {
                sum
                showMode
                period
                start
                finish
            }
        }
        type
        name
        id
        currency
    }
    ${recommendationsDailyBudgetGridKpiItems}
    ${recommendationKeyFragment}
`;

export const removeFromBlackListRecommendationKpiFragment = gql`
    fragment RemoveFromBlackListRecommendationKpiFragment on GdRecommendationKpiRemovePagesFromBlackList {
        clicks
        cost
        currency
        pageName
    }
`;

export const removeFromBlackListRecommendationsGridRow = gql`
    fragment RemoveFromBlackListRecommendationsGridRowFragment on GdPage {
        name
        recommendations {
            status
            isApplicable
            keys {
                ...recommendationKeyFragment
            }
            ... on GdRecommendationWithKpi {
                kpi {
                    ...RemoveFromBlackListRecommendationKpiFragment
                }
            }
        }
    }
    ${removeFromBlackListRecommendationKpiFragment}
    ${recommendationKeyFragment}
`;

export const recommendationsPageQuery = gql`
    query RecommendationsPageQuery($login: String!) {
        reqId: getReqId
        client(searchBy: { login: $login }) {
            recommendationsSummary {
                rowset {
                    ...summaryRecommendationFragment
                }
            }
        }
    }
    ${summaryRecommendationFragment}
`;

export const dailyBudgetRecommendationsGridQuery = gql`
    query DailyBudgetRecommendationsGridQuery(
        $login: String!
        $campaignInput: GdCampaignsContainerInput!
    ) {
        reqId: getReqId
        client(searchBy: { login: $login }) {
            features {
                ...RecommendationsGridClientFeaturesFragment
            }
            info {
                id
                workCurrency
            }
            campaigns(input: $campaignInput) {
                rowset {
                    ...RecommendationsDailyBudgetCampaignsRowFragment
                }
                totalCount
                campaignIds
            }
        }
    }
    ${recommendationsDailyBudgetCampaignsRow}
    ${recommendationsGridClientFeaturesFragment}
`;

export const removePagesFromBlackListRecommendationsGridQuery = gql`
    query RemovePagesFromBlackListRecommendationGridQuery(
        $login: String!
        $offset: Int = 0
        $orderBy: [GdPageOrderByInput!]
        $count: Int = 10000
    ) {
        reqId: getReqId
        client(searchBy: { login: $login }) {
            features {
                ...RecommendationsGridClientFeaturesFragment
            }
            info {
                id
                workCurrency
            }
            forbiddenPages(
                limitOffset: { offset: $offset, limit: $count }
                orderBy: $orderBy
            ) {
                totalCount
                rowset {
                    ...RemoveFromBlackListRecommendationsGridRowFragment
                }
            }
        }
    }
    ${removeFromBlackListRecommendationsGridRow}
    ${recommendationsGridClientFeaturesFragment}
`;

export const switchOnAutotargetingRecommendationsGridRow = gql`
    fragment SwitchOnAutotargetingRecommendationsGridRowFragment on GdAdGroup {
        stats {
            clicks
            cost
        }
        recommendations {
            status
            isApplicable
            ... on GdRecommendationWithKpi {
                kpi {
                    clicks
                    cost
                }
            }
            keys {
                ...recommendationKeyFragment
            }
        }
        name
        id
        campaign {
            id
            name
        }
    }

    ${recommendationKeyFragment}
`;

// важно, что в запросе фильтр по типу рекомендации нужен как на уровне groups,
// так и на уровне campaigns.
// Это нужно, чтобы сервер корректно вернул статистику, так как он сначала запрашивает кампании
// и статистику по заданному периоду, а потом группы, которые он фильтрует по типу рекомендации
// (вообщем небольшой серверный костылек)
export const switchOnAutotargetingRecommendationsGridQuery = gql`
    query SwitchOnAutotargetingRecommendationsGridQuery(
        $login: String!
        $adGroupInput: GdAdGroupsContainerInput!
    ) {
        reqId: getReqId
        client(searchBy: { login: $login }) {
            features {
                ...RecommendationsGridClientFeaturesFragment
            }
            info {
                id
                workCurrency
            }
            adGroups(input: $adGroupInput) {
                rowset {
                    ...SwitchOnAutotargetingRecommendationsGridRowFragment
                }
                totalCount
            }
        }
    }
    ${switchOnAutotargetingRecommendationsGridRow}
    ${recommendationsGridClientFeaturesFragment}
`;

export const recommendationsGridSummaryRowFragment = gql`
    fragment recommendationsGridSummaryRowFragment on GdRecommendationSummary {
        type
        status
        isApplicable
        totalCount
        keys {
            ...recommendationKeyFragment
        }
        ... on GdRecommendationSummaryWithKpi {
            kpi {
                ...summaryRecommendationKpiFragment
                ... on GdRecommendationKpiDailyBudget {
                    recommendedDailyBudget
                }
            }
        }
    }

    ${summaryRecommendationKpiFragment}
    ${recommendationKeyFragment}
`;

export const recommendationsGridSummaryQuery = gql`
    query RecommendationsGridSummaryQuery(
        $login: String!
        $types: [GdiRecommendationTypeInput]
    ) {
        reqId: getReqId
        client(searchBy: { login: $login }) {
            recommendationsSummary(filter: $types) {
                rowset {
                    ...recommendationsGridSummaryRowFragment
                }
            }
        }
    }

    ${recommendationsGridSummaryRowFragment}
`;

export const recommendationsStatuses = gql`
    query RecommendationsStatusesQuery(
        $login: String!
        $keys: [GdRecommendationKeyInput!]
    ) {
        reqId: getReqId
        client(searchBy: { login: $login }) {
            recommendationsStatuses(keys: $keys) {
                status
                cid
            }
        }
    }
`;

export const campaignsIdsQuery = gql`
    query CampaignsIdsQuery(
        $login: String!
        $campaignInput: GdCampaignsContainerInput!
    ) {
        reqId: getReqId
        client(searchBy: { login: $login }) {
            campaigns(input: $campaignInput) {
                rowset {
                    id
                }
            }
        }
    }
`;

export const campaignRecommendationsQuery = gql`
    query CampaignRecommendationsQuery($login: String!, $campaignId: Long!) {
        reqId: getReqId
        client(searchBy: { login: $login }) {
            campaigns(
                input: {
                    statRequirements: { preset: TODAY }
                    orderBy: []
                    limitOffset: { limit: 1, offset: 0 }
                    filter: { campaignIdIn: [$campaignId] }
                }
            ) {
                rowset {
                    recommendations {
                        status
                        isApplicable
                        ...RecommendationsDailyBudgetGridKpiItemsFragment
                        keys {
                            ...recommendationKeyFragment
                        }
                    }
                }
            }
        }
    }

    ${recommendationsDailyBudgetGridKpiItems}
    ${recommendationKeyFragment}
`;
